import type { ReactNode } from 'react';
import './Alert.css';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const ICONS: Record<AlertTone, string> = {
  info: 'ℹ️',
  success: '✅',
  warning: '⚠️',
  danger: '⚠️',
};

const LABELS: Record<AlertTone, string> = {
  info: 'Information',
  success: 'Success',
  warning: 'Warning',
  danger: 'Error',
};

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
}

/**
 * Status message. The tone is carried by an icon *and* a text label, never by
 * colour alone (section 12).
 */
export function Alert({ tone = 'info', title, children }: AlertProps) {
  const isUrgent = tone === 'danger' || tone === 'warning';

  return (
    <div
      className={`sc-alert sc-alert--${tone}`}
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
    >
      <span className="sc-alert__icon" aria-hidden="true">
        {ICONS[tone]}
      </span>
      <div className="sc-alert__content">
        <strong className="sc-alert__title">{title ?? LABELS[tone]}</strong>
        <div className="sc-alert__body">{children}</div>
      </div>
    </div>
  );
}
